import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, memberProfileAPI } from '../services/api';
import { User } from '../types';
import { MemberProfile } from '../types/memberProfile';
import LogRocket from 'logrocket';

/**
 * Enum to track what triggered a member profile refresh
 * This helps with debugging and understanding the refresh lifecycle
 */
export enum MemberProfileRefreshTrigger {
  LOGIN = 'login',
  APP_INIT = 'app_init',
  PROFILE_SWITCH = 'profile_switch',
  MANUAL_REFRESH = 'manual_refresh',
  NAVIGATION_TO_PROFILE = 'navigation_to_profile',
  ADMIN_UPDATE_DETECTED = 'admin_update_detected',
  PERIODIC_CHECK = 'periodic_check'
}

interface AuthContextType {
  user: User | null;
  selectedMemberProfile: MemberProfile | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  setSelectedMemberProfile: (profile: MemberProfile | null) => void;
  refreshSelectedMemberProfile: (trigger?: MemberProfileRefreshTrigger) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Centralized function to refresh the selected member profile from the backend
   * 
   * Member Profile Refresh Triggers:
   * 
   * IMPLEMENTED (Automatic):
   * - LOGIN: After successful login (see login function)
   * - APP_INIT: On app initialization if cached profile exists (see checkLoggedIn useEffect)
   * 
   * FUTURE (To be implemented):
   * - PERIODIC_CHECK: Every 5 minutes while user is active
   * - NAVIGATION_TO_PROFILE: When user navigates to their profile page
   * - ADMIN_UPDATE_DETECTED: After admin updates detected via polling
   * - MANUAL_REFRESH: User explicitly refreshes
   * - PROFILE_SWITCH: When user switches between member profiles
   * 
   * @param trigger - Optional trigger type for logging/debugging
   */
  const refreshSelectedMemberProfile = async (trigger?: MemberProfileRefreshTrigger) => {
    if (!selectedMemberProfile?.id) {
      console.log('[MemberProfile] No profile to refresh');
      return;
    }

    try {
      console.log(`[MemberProfile] Refreshing profile due to: ${trigger || 'unknown'}`);
      
      const response = await memberProfileAPI.getMemberProfileById(selectedMemberProfile.id);
      const freshProfile = response.data;
      
      setSelectedMemberProfile(freshProfile);
      localStorage.setItem('selectedMemberProfile', JSON.stringify(freshProfile));
      
      console.log('[MemberProfile] Profile refreshed successfully', {
        trigger,
        profileId: freshProfile.id,
        membershipTier: freshProfile.membershipTier?.name
      });
    } catch (err: any) {
      console.error('[MemberProfile] Failed to refresh profile:', err);
      // Don't throw - we don't want refresh failures to break the app
      // The user will continue with the cached profile
    }
  };

  // Check if the user is already logged in
  useEffect(() => {
    const checkLoggedIn = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token) {
        try {
          // First try to use the stored user data to avoid an API call
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            // Identify user in LogRocket on app load
            LogRocket.identify(parsedUser.id.toString(), {
              name: parsedUser.name || parsedUser.email,
              email: parsedUser.email,
              role: parsedUser.role,
              tenantId: parsedUser.tenantId || 'none'
            });
          }

          // Then try to refresh user data from the API
          const response = await authAPI.getCurrentUser();
          const refreshedUser = response.data.user;
          setUser(refreshedUser);
          // Update LogRocket identification with fresh data
          LogRocket.identify(refreshedUser.id.toString(), {
            name: refreshedUser.name || refreshedUser.email,
            email: refreshedUser.email,
            role: refreshedUser.role,
            tenantId: refreshedUser.tenantId || 'none'
          });
          
          // Check if there's a cached member profile and refresh it from the API
          const storedMemberProfile = localStorage.getItem('selectedMemberProfile');
          if (storedMemberProfile) {
            try {
              const cachedProfile = JSON.parse(storedMemberProfile);
              // Set the cached profile temporarily to allow refreshSelectedMemberProfile to work
              setSelectedMemberProfile(cachedProfile);
              
              // Fetch fresh profile data from backend
              console.log('[MemberProfile] Found cached profile, fetching fresh data...');
              const profileResponse = await memberProfileAPI.getMemberProfileById(cachedProfile.id);
              const freshProfile = profileResponse.data;
              
              setSelectedMemberProfile(freshProfile);
              localStorage.setItem('selectedMemberProfile', JSON.stringify(freshProfile));
              
              console.log('[MemberProfile] Profile refreshed on app init', {
                trigger: MemberProfileRefreshTrigger.APP_INIT,
                profileId: freshProfile.id,
                membershipTier: freshProfile.membershipTier?.name
              });
            } catch (profileErr: any) {
              console.error('[MemberProfile] Failed to refresh profile on app init:', profileErr);
              // Keep the cached profile if refresh fails
              const cachedProfile = JSON.parse(storedMemberProfile);
              setSelectedMemberProfile(cachedProfile);
            }
          }
        } catch (err: any) {
          console.error('Error fetching user data:', err);
          // Don't immediately remove tokens on error - only if it's a 401
          if (err.response && err.response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      }

      setLoading(false);
    };

    checkLoggedIn();
  }, []);
  // Login function
  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.login(email, password, rememberMe);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // For non-super-admin users, always set tenantId from user
      // For super-admin users, only set it if there's no existing selection
      if (user.role !== 'super_admin') {
        if (user.tenantId) {
          localStorage.setItem('tenantId', user.tenantId);
        }
      } else if (!localStorage.getItem('tenantId') && user.tenantId) {
        // For super admins, only set tenantId if not already set (preserves tenant selection through login)
        localStorage.setItem('tenantId', user.tenantId);
      }

      // Identify user in LogRocket
      LogRocket.identify(user.id.toString(), {
        name: user.name || user.email,
        email: user.email,
        role: user.role,
        tenantId: user.tenantId || 'none'
      });

      setUser(user);
      
      // Check if user had a previously selected member profile and refresh it
      const storedMemberProfile = localStorage.getItem('selectedMemberProfile');
      if (storedMemberProfile) {
        try {
          const cachedProfile = JSON.parse(storedMemberProfile);
          // Set cached profile temporarily
          setSelectedMemberProfile(cachedProfile);
          
          // Fetch fresh profile data from backend
          console.log('[MemberProfile] Refreshing cached profile after login...');
          const profileResponse = await memberProfileAPI.getMemberProfileById(cachedProfile.id);
          const freshProfile = profileResponse.data;
          
          setSelectedMemberProfile(freshProfile);
          localStorage.setItem('selectedMemberProfile', JSON.stringify(freshProfile));
          
          console.log('[MemberProfile] Profile refreshed after login', {
            trigger: MemberProfileRefreshTrigger.LOGIN,
            profileId: freshProfile.id,
            membershipTier: freshProfile.membershipTier?.name
          });
        } catch (profileErr: any) {
          console.error('[MemberProfile] Failed to refresh profile after login:', profileErr);
          // Keep the cached profile if refresh fails
        }
      }
      
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to login');
      setLoading(false);
      throw new Error(err.response?.data?.message || 'Failed to login');
    }
  };

  // Register function
  const register = async (userData: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.register(userData);

      setLoading(false);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register');
      setLoading(false);
      throw new Error(err.response?.data?.message || 'Failed to register');
    }
  };
  // Logout function
  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('selectedMemberProfile');

    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Error logging out:', err);
    }

    setUser(null);
    setSelectedMemberProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        selectedMemberProfile,
        loading,
        error,
        login,
        register,
        logout,
        setSelectedMemberProfile,
        refreshSelectedMemberProfile,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
